import { useEditorReducer } from '@components/BlinqImageEditor';
import { colorHexToNumber, numberToColorHex } from '@components/BlinqImageEditor/helpers';
import type { ImageQuality, ImageType } from '@components/Card2/CardHeader/types';
import { getDefaultValueProviderForCardImage } from '@components/Card2/CardHeader/utilities';
import type { CardFlavour } from '@components/Card2/types';
import { notificationContext } from '@context/notifications';
import { useRemoteConfigNumber } from '@hooks/use-remote-config-number';
import { analytics } from '@modules/analytics';
import { getImageDimensions, uploadImageAsync } from '@modules/image-helpers';
import { blinqDatadogRum } from '@modules/monitoring/rum';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import type { CardImage, ValueProvider } from '.cache/__types__';

export interface PicturePopoverCallbacks {
  onClose: () => void;
  onUpdate: (imageReference: Partial<CardImage>, quality?: ImageQuality) => void;
  onSelect: (imageReference: Partial<CardImage>) => void;
  onSelectOpen?: (imageType: ImageType) => void;
  onRemove: () => void;
  onSave: (imageUrl: Partial<CardImage>) => void;
}

interface UseEditPicturePopoverArgs {
  type: ImageType;
  imageReference: Partial<CardImage> | undefined | null;
  show: boolean;
  isTemplate: boolean;
  templateId: string | undefined | null;
  userId: string | undefined | null;
  workspaceId: string | undefined | null;
  cardId: string | undefined | null;
  flavour: CardFlavour | undefined;
}
export const useEditPicturePopover = (
  { type, imageReference, show, flavour }: UseEditPicturePopoverArgs,
  { onClose, onUpdate, onSelect, onSelectOpen, onRemove, onSave }: PicturePopoverCallbacks,
) => {
  const canLock = true;

  const { notify } = useContext(notificationContext);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [showUnlinkWarning, setShowUnlinkWarning] = useState(false);
  const [editedImageReference, setEditedImageReference] = useState<Partial<CardImage> | undefined | null>(
    imageReference,
  );

  const isFieldLinkedToProfile = editedImageReference?.value_provider === 'profile';

  // State var to store the image URLs while switching between Shared and Individual.
  const [tempImage, setTempImage] = useState<Partial<CardImage> | undefined>(undefined);

  const [fieldsChanged, setFieldsChanged] = useState(false);
  const [saving, setSaving] = useState(false);
  const originalLockStateRef = useRef<{ isLocked: boolean }>(undefined);

  const hasInitialisedRef = useRef(false);

  const isLocked = !!imageReference?.is_locked;

  const smallestDimension = useRemoteConfigNumber('min_card_img_dimension', 200);
  const defaultEditorState = useMemo(() => {
    return {
      id: imageReference?.id,
      zoom: imageReference?.metadata?.zoom,
      backgroundColor: imageReference?.metadata?.background_color
        ? colorHexToNumber(imageReference?.metadata?.background_color)
        : null,
      position: {
        x: imageReference?.metadata?.img_position_x ?? 0,
        y: imageReference?.metadata?.img_position_y ?? 0,
      }, // origin
    };
  }, [imageReference]);

  const [editorState, updateEditorState] = useEditorReducer(defaultEditorState);

  // Keep editedImageReference in sync with imageReference
  useEffect(() => {
    const defaultValueProvider = getDefaultValueProviderForCardImage(
      flavour,
      type,
      imageReference ?? undefined,
    );
    setEditedImageReference({
      ...imageReference,
      value_provider: imageReference?.value_provider ?? defaultValueProvider,
    });
  }, [flavour, imageReference, type]);

  const setFieldsChangedOnFirstEdit = () => {
    // When the edit UI pops up and "Individual" is preselected, we need to enable the Save button
    // so you can save the dialog
    if (editedImageReference?.value_provider === 'member' && !imageReference?.value_provider) {
      setFieldsChanged(true);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(setFieldsChangedOnFirstEdit, [editedImageReference, imageReference]);

  useEffect(() => {
    if (defaultEditorState && !editorState?.zoomBounds) {
      updateEditorState({ type: 'RESET', state: defaultEditorState });
    }
  }, [defaultEditorState, editorState?.zoomBounds, updateEditorState]);

  useEffect(() => {
    if (!hasInitialisedRef.current) {
      hasInitialisedRef.current = true;
      return;
    }
    if (!show) {
      setEditedImageReference(undefined);
      setTempImage(undefined);
      setShowUnlinkWarning(false);
      setShowDeleteWarning(false);
      setFieldsChanged(false);
    }
  }, [show]);

  useEffect(() => {
    if (show && originalLockStateRef.current === undefined) {
      originalLockStateRef.current = { isLocked };
    } else if (!show) {
      originalLockStateRef.current = undefined;
    }
  }, [show, isLocked]);

  function handleDelete() {
    analytics.track({
      name: 'removepicture_confirmed',
      properties: { module: 'card_designer', card_picture_type: type },
    });
    onRemove();
    handleClosePopover();
  }

  function handleCancel() {
    analytics.track({ name: 'canceled', properties: { module: 'image_editor' } });

    handleClosePopover();
  }

  function handleImageSelect(imageReference: Partial<CardImage>) {
    if (imageReference.original_url) {
      analytics.track({
        name: 'addpicture_selected',
        properties: {
          module: 'card_designer',
          picture_select_method: 'choose_from_library',
          card_picture_type: type,
        },
      });
      getImageDimensions(imageReference.original_url).then((dimensions) => {
        if (dimensions[0] < smallestDimension || dimensions[1] < smallestDimension) {
          notify({
            title: 'Small image',
            type: 'warning',
            message: 'This image is smaller than recommended',
            autoHideAfterTimeout: true,
            timeout: 5000,
            size: 'small',
          });
        }
      });
    }
    onSelect(imageReference);
    setFieldsChanged(true);
  }

  useEffect(() => {
    if (show && imageReference?.edited_url === undefined && imageReference?.metadata === undefined) {
      onSelectOpen?.(type);
    }
  }, [show, imageReference, type, onSelectOpen]);

  const handleLowQualityImageUpdate = useCallback(
    (edited_url?: string | undefined) => {
      if (editedImageReference && edited_url) {
        setEditedImageReference((prevState) => {
          const newEditedImageReference = {
            ...prevState,
            edited_url,
            metadata: {
              ...prevState?.metadata,
              editor_version: editorState.editorVersion,
            },
          };
          onUpdate(newEditedImageReference, 'low');
          return newEditedImageReference;
        });

        setFieldsChanged(true);
      }
    },
    [editedImageReference, editorState.editorVersion, onUpdate],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  const handleHighQualityImageUpdate = useCallback(
    (edited_url?: string | undefined) => {
      if (editedImageReference && edited_url) {
        setEditedImageReference((prevState) => {
          const newEditedImageReference = {
            ...prevState,
            edited_url,
            metadata: {
              ...prevState?.metadata,
              editor_version: editorState.editorVersion,
            },
          };
          onUpdate(newEditedImageReference, 'high');
          return newEditedImageReference;
        });
        setFieldsChanged(true);
      }
    },
    [onUpdate, setFieldsChanged, setEditedImageReference, editedImageReference, editorState.editorVersion],
  );

  const handleSetValueProvider = useCallback(
    (valueProvider: ValueProvider) => {
      if (!editedImageReference) {
        return;
      }

      const newImageRef = {
        ...editedImageReference,
        metadata: {
          ...editedImageReference.metadata,
        },
        value_provider: valueProvider,
      };

      if ((valueProvider === 'template' || valueProvider === 'admin') && tempImage) {
        newImageRef.edited_url = tempImage.edited_url;
        newImageRef.original_url = tempImage.original_url;

        // Clear these when switching to Template or Admin because those toggle are hidden in those cases
        newImageRef.is_optional = false;
        newImageRef.instruction_text = null;
      }

      if (valueProvider === 'member') {
        setTempImage(editedImageReference);
        newImageRef.edited_url = null;
        newImageRef.original_url = null;
      }

      setEditedImageReference(newImageRef);
      onUpdate(newImageRef);
      setFieldsChanged(true);
    },
    [editedImageReference, onUpdate, tempImage],
  );

  const handleSetInstruction = useCallback(
    (instruction: string | undefined | null) => {
      if (editedImageReference) {
        const newImageRef = {
          ...editedImageReference,
          metadata: {
            ...editedImageReference.metadata,
          },
          instruction_text: instruction,
        };
        setEditedImageReference(newImageRef);
        onUpdate(newImageRef);
        setFieldsChanged(true);
      }
    },
    [editedImageReference, onUpdate],
  );

  const handleSetIsOptional = useCallback(
    (isOptional: boolean) => {
      if (editedImageReference) {
        const newImageRef = {
          ...editedImageReference,
          metadata: {
            ...editedImageReference.metadata,
          },
          is_optional: isOptional,
        };
        setEditedImageReference(newImageRef);
        onUpdate(newImageRef);
        setFieldsChanged(true);
      }
    },
    [editedImageReference, onUpdate],
  );

  /** Resets the popover state and closes it */
  function handleClosePopover() {
    setEditedImageReference(undefined);
    setTempImage(undefined);
    updateEditorState({ type: 'RESET' });
    onClose();
  }

  function handleSaveError(e: unknown) {
    console.error(e);
    notify({
      type: 'warning',
      timeout: 8000,
      title: 'Image upload failed',
      message:
        'We were not able to upload and save your image. Please check your internet connection and try again in a few minutes.',
    });

    setSaving(false);
  }

  function handleSaveSuccess(
    edited_url: string | null,
    original_url: string | null,
    reference: Partial<CardImage>,
  ) {
    onSave({
      id: reference?.id,
      edited_url,
      original_url,
      attribute_type: reference.attribute_type,
      value_provider: reference.value_provider,
      instruction_text: reference.instruction_text,
      is_optional: reference.is_optional,
      metadata: {
        img_position_x: editorState.position?.x,
        img_position_y: editorState.position?.y,
        background_color:
          editorState.backgroundColor !== null && editorState.backgroundColor !== undefined
            ? numberToColorHex(editorState.backgroundColor)
            : undefined,
        zoom: editorState.zoom || 1,
        editor_version: editorState.editorVersion,
      },
    });
    analytics.track({ name: 'imagesaved', properties: { module: 'image_editor' } });
    setSaving(false);

    handleClosePopover();
  }

  const imageUploadCount = useRef(0);

  function performSave() {
    const reference = editedImageReference || imageReference; // take a local reference for closure.

    if (!reference) {
      // The lock state has change but there is no image, i.e. the user doesn't want an image to be added
      setSaving(false);
      onClose();
      return;
    }

    // Change field `value_provider` from 'profile' to 'member', only if the fields have changed.
    if (isFieldLinkedToProfile && fieldsChanged) {
      reference.value_provider = 'member';
    }

    setSaving(true);

    // If there is no image, we don't need to upload anything.
    if (!reference.edited_url) {
      handleSaveSuccess(null, null, reference);
      return;
    }

    // upload in parallel
    // todo: add upload progress indicators

    imageUploadCount.current++;
    const currentUploadCount = imageUploadCount.current;
    blinqDatadogRum.startDurationVital(`imageUpload${currentUploadCount}`);
    const editedPromise = uploadImageAsync(reference.edited_url, { onProgressUpdate: undefined });
    const originalPromise = uploadImageAsync(reference.original_url, {
      onProgressUpdate: undefined,
    });

    Promise.all([editedPromise, originalPromise])
      .then(([edited, original]) => {
        handleSaveSuccess(edited, original, reference);
        blinqDatadogRum.stopDurationVital(`imageUpload${currentUploadCount}`);
      })
      .catch(handleSaveError);
  }

  function handleSave() {
    if (isFieldLinkedToProfile) {
      setShowUnlinkWarning(true);
    } else performSave();
  }

  function handleCancelDeleteWarning() {
    setShowDeleteWarning(false);
  }

  function handleDeletePress() {
    analytics.track({ name: 'removepicture_clicked', properties: { module: 'card_designer' } });
    setShowDeleteWarning(true);
  }

  function handleCancelUnlink() {
    setShowUnlinkWarning(false);
  }

  function handleConfirmUnlink() {
    if (!isFieldLinkedToProfile) return;

    performSave();
    setShowUnlinkWarning(false);
  }

  function handleToggleLock() {
    setFieldsChanged(true);
  }

  return {
    canLock,
    editorState,
    fieldsChanged,
    isLocked,
    editedImageReference,
    saving,
    setFieldsChanged,
    showDeleteWarning,
    showUnlinkWarning,
    updateEditorState,
    isFieldLinkedToProfile,
    handlers: {
      handleDelete,
      handleCancel,
      handleImageSelect,
      handleLowQualityImageUpdate,
      handleHighQualityImageUpdate,
      handleClosePopover,
      handleSave,
      handleCancelDeleteWarning,
      handleDeletePress,
      handleToggleLock,
      handleCancelUnlink,
      handleConfirmUnlink,
      handleSetValueProvider,
      handleSetInstruction,
      handleSetIsOptional,
      setShowUnlinkWarning,
    },
  };
};
